import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { IInvoiceParams, PRIMARY_CONTACT_NAME_SORT_COLUMN } from "shared";

import { parseCurrencyFilter } from "./currencyHelpers";
import { numberFilterParams } from "./datagrid";

export const sortColumnMap: Record<string, string> = {
  invoiceNumber: `xero_number`,
  invoiceReference: `xero_reference`,
  invoiceStatus: `xero_status`,
  contactName: `xero_contact_name`,
  invoiceIssueDate: `issue_date_string`,
  invoiceDueDate: `due_date_string`,
  invoiceTotal: `xero_amount_total`,
  invoiceOutstanding: `xero_amount_due`,
  frozen: `skip`,
  groupId: ``,
  IsLateFee: `created_by_us`,
  daysOverdue: `days_overdue`,
  invoiceSentStatus: `sent_to_customer`,
  currency: `xero_currency`,
  primaryContactName: PRIMARY_CONTACT_NAME_SORT_COLUMN,
  hasPaymentPlan: ``,
  hasPaymentPromise: ``,
  hasOpenQuery: ``,
  expectedPaymentDate: `expected_payment_date_str`,
  invoiceTag: ``,
};

interface InvoicePaginationInput {
  page: number;
  pageSize: number;
}

function toDateOnlyString(value: unknown) {
  if (!value || typeof value !== `string`) {
    return null;
  }

  return value.split(`T`)[0] || null;
}

function toNullableNumber(value: unknown) {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === ``) {
    return null;
  }

  const parsedValue = Number(value);

  return Number.isNaN(parsedValue) ? null : parsedValue;
}

export function buildInvoiceListParamsFromFilterModel(
  filterModel: GridFilterModel,
  sortModel?: GridSortModel,
  pagination?: InvoicePaginationInput,
): IInvoiceParams {
  const page = pagination ? pagination.page + 1 : 1;
  const limit = pagination?.pageSize ?? 100;
  const status = filterModel.items.find(item => item.field === `invoiceStatus`)?.value || null;
  const frozen = filterModel.items.find(item => item.field === `frozen`)?.value;
  const groupId = filterModel.items.find(item => item.field === `groupId`)?.value || null;
  const contactId = filterModel.items.find(item => item.field === `contactId`)?.value || null;
  const reference = filterModel.items.find(item => item.field === `invoiceReference`)?.value || null;
  const number = filterModel.items.find(item => item.field === `invoiceNumber`)?.value || null;
  const contactName = filterModel.items.find(item => item.field === `contactName`)?.value || null;
  const primaryContactName = filterModel.items.find(item => item.field === `primaryContactName`)?.value || null;
  const minDueDate = filterModel.items.find(item => item.field === `invoiceDueDate` && item.operator === `onOrAfter`)?.value || null;
  const maxDueDate = filterModel.items.find(item => item.field === `invoiceDueDate` && item.operator === `onOrBefore`)?.value || null;
  const minIssueDate = filterModel.items.find(item => item.field === `invoiceIssueDate` && item.operator === `onOrAfter`)?.value || null;
  const maxIssueDate = filterModel.items.find(item => item.field === `invoiceIssueDate` && item.operator === `onOrBefore`)?.value || null;
  const minExpectedPaymentDate = filterModel.items.find(item => item.field === `expectedPaymentDate` && item.operator === `onOrAfter`)?.value || null;
  const maxExpectedPaymentDate = filterModel.items.find(item => item.field === `expectedPaymentDate` && item.operator === `onOrBefore`)?.value || null;
  const isLateFee = filterModel.items.find(item => item.field === `IsLateFee`)?.value;
  const quickSearchStr = filterModel.quickFilterValues?.[0] || null;
  const sentToCustomer = filterModel.items.find(item => item.field === `invoiceSentStatus`)?.value;
  const currency = filterModel.items.find(item => item.field === `currency`)?.value || null;
  const hasPaymentPlan = filterModel.items.find(item => item.field === `hasPaymentPlan`)?.value;
  const hasPaymentPromise = filterModel.items.find(item => item.field === `hasPaymentPromise`)?.value;
  const hasOpenQuery = filterModel.items.find(item => item.field === `hasOpenQuery`)?.value;
  const invoiceTag = filterModel.items.find(item => item.field === `invoiceTag`)?.value || null;
  const parsedContactId = toNullableNumber(contactId);
  let sortColumn = sortModel?.[0]?.field || null;
  const sortOrder = sortModel?.[0]?.sort || null;

  if (sortColumn && sortColumn in sortColumnMap) {
    sortColumn = sortColumnMap[sortColumn];
  }

  return {
    page,
    limit,
    invoiceTextSearch: quickSearchStr,
    xeroStatus: status,
    frozen,
    groupId,
    contactId: parsedContactId as number | null | undefined,
    reference,
    number,
    contactName,
    primaryContactName,
    dueDateMinDate: toDateOnlyString(minDueDate),
    dueDateMaxDate: toDateOnlyString(maxDueDate),
    issueDateMinDate: toDateOnlyString(minIssueDate),
    issueDateMaxDate: toDateOnlyString(maxIssueDate),
    expectedPaymentDateMinDate: toDateOnlyString(minExpectedPaymentDate),
    expectedPaymentDateMaxDate: toDateOnlyString(maxExpectedPaymentDate),
    ...numberFilterParams(filterModel.items, `invoiceTotal`, `totalAmount`, parseCurrencyFilter),
    ...numberFilterParams(filterModel.items, `invoiceOutstanding`, `dueAmount`, parseCurrencyFilter),
    sortColumn,
    sortType: sortOrder,
    isLateFee,
    // A single row keeps the legacy value/operator params; two rows (e.g. an aging bucket) send min/max
    ...numberFilterParams(filterModel.items, `daysOverdue`, `daysOverdue`, value => toNullableNumber(value) ?? undefined),
    sentToCustomer,
    currency,
    hasPaymentPlan,
    hasPaymentPromise,
    hasOpenQuery,
    invoiceTag,
  };
}
